/* eslint-disable unicorn/prefer-ternary,no-underscore-dangle */
import _ from 'lodash-es';
import APIV5 from '~/models/api';
import urlcat from 'urlcat';
import Defaults from '~/scripts/constants/defaults';
import { UserModel } from '~/scripts/services/model/user';
import Utils from '~/scripts/services/utils';

/* globals Knitiv */

/**
	* @class API
	*/
export default class API {
	/** @type {number | undefined} */
	#displayRetries;

	/** @type {number} */
	#nbAttempts;

	/** @type {any} */
	#nextRequestKParam;

	/* @ngInject */
	/**
		*
		* @param {import('angular').IFilterService} $filter
		* @param {import('angular').IHttpService} $http
		* @param {import('angular').ILocationService} $location
		* @param {import('angular').IQService} $q
		* @param {Knitiv.RootScope} $rootScope
		* @param {import('angular').ITimeoutService} $timeout
		* @param {import('angular').translate.ITranslateService} $translate
		* @param {*} Notification
		* @param {*} User
		* @param {*} Reset
		*/
	constructor(
		$filter,
		$http,
		$location,
		$q,
		$rootScope,
		$timeout,
		$translate,
		Notification,
		User,
		Reset,
	) {
		/** @type {number | undefined} */
		this.#displayRetries = undefined;

		/** @type {number} */
		this.#nbAttempts = 3;

		this.#nextRequestKParam = null;

		this.$filter = $filter;
		this.$http = $http;
		this.$location = $location;
		this.$q = $q;
		this.$rootScope = $rootScope;
		this.$timeout = $timeout;
		this.$translate = $translate;
		this.Notification = Notification;
		this.Reset = Reset;
		/** @type {UserModel} */
		this.User = User;

		this.$rootScope.$on('API.logout', async (event, reason) => {
			await APIV5.instance.logout({ id: 'api-logout' });
			await this.User.logout(reason);
		});
	}

	// @ts-ignore
	// eslint-disable-next-line no-undef
	#backoff(retries, cfg_, function_, delay = 5000) {
		// multiply timeout by current attempt
		cfg_.timeout *= (this.#nbAttempts - retries) + 1;
		return function_(cfg_)
			.catch((/** @type {any} */ error) => {
				if (error.status === -1 && error.xhrStatus === 'error') {
					if (retries > 1) {
						if (this.#displayRetries !== retries) {
							this.#displayRetries = retries;
							this.Notification.warning({
								title: this.$translate.instant('error.unable-to-process'),
								message: this.$filter('sprintf')(
									this.$translate.instant('error.network-attempt'),
									this.#nbAttempts - retries + 2,
									this.#nbAttempts,
									delay / 1000,
								),
								onClose: () => {
									this.#displayRetries = undefined;
								},
							});
						}
						return this.$timeout(() => this.#backoff(retries - 1, cfg_, function_, delay * 2), delay);
					}

					this.Notification.error({
						title: this.$translate.instant('error.title'),
						message: this.$translate.instant('error.network-disconnected'),
						replaceMessage: true,
					});
					return this.$q.reject(error);
				}

				return this.$q.reject(error);
			});
	}

	/**
	 * @typedef {Object} ExtraConfig
	 * @property {number} [timeout]
	 * @property {'file' | 'back'} [endpoint]
	 * @property {string} [responseType]
	 * @property {Object.<string, any>} [eventHandlers]
	 * @property {any} [promiseTimeout]
	 * @property {string} [cacheKey]
	 * @property {boolean} [nocache]
	 * @property {boolean} [hideNotification]
	 * @property {string} [id] - Identify more clearly from where come the request
	 */

	// All functions belows describe the calls to API
	// They are sorted alphabetically

	/**
	 * @param {any} request
	 * @param {ExtraConfig} [cfg]
	 * @returns {Promise<any>}
	 */
	docExtractFieldsGet(request, cfg = {}) {
		return this._request('DOCExtractFieldsGet', request, cfg);
	}

	/**
	 * @param {any} items
	 * @param {ExtraConfig} [cfg]
	 * @returns {Promise<any>}
	 */
	exportTemplate(items, cfg = {}) {
		return this._request('ExportTemplateDataset', {
			items,
		}, cfg);
	}

	fileKonvProcessStart() {
		return this._request('FileKonvProcessStart');
	}

	fileKonvStatus() {
		return this._request('FileKonvStatus');
	}

	/**
	 * @param {?import('@knitiv/api-client-javascript').KNode} treeKid
	 * @param {any[] | undefined} relpath
	 * @param {ExtraConfig} [cfg]
	 * @returns {Promise<any>}
	 */
	kFileTreeGet(treeKid, relpath, cfg = {}) {
		return this._request('KFILE_Tree_get', {
			tree_kid: treeKid,
			relpath,
		}, cfg);
	}

	/**
	 * @param {string} log
	 * @param {number} nb
	 * @param {ExtraConfig} [cfg]
	 * @returns {Promise<any>}
	 */
	log(log, nb, cfg = {}) {
		// return $http.get('/kniKonvService.log', {
		// 	transformResponse: defaults => defaults.split('\n')
		// }).then(res => $q.resolve({lines: angular.isDefined(nb) ? res.data.slice(-nb) : res.data}));
		return this._request('Log', {
			log,
			nb,
		}, cfg);
	}

	/**
	 * @param {any} items
	 * @param {ExtraConfig} [cfg]
	 * @returns {Promise<any>}
	 */
	objectImportJSON(items, cfg = {}) {
		return this._request('ObjectImportJson', {
			items,
		}, cfg);
	}

	/**
	 * Send request to backend
	 *
	 * @param {string} resource Resource identifier
	 * @param {any} [parameters] Parameters
	 * @param {Object|ExtraConfig} [cfg_] Special config
	 * @returns {Promise<any>} Promise result
	 */
	// @ts-ignore
	// eslint-disable-next-line no-undef
	_request(resource, parameters = {}, cfg_ = {}) {
		const cfg = _.merge({
			headers: {
				'Content-Type': 'application/json; charset=UTF-8',
			},
			timeout: Defaults.requestTimeout,
			endpoint: 'back',
		}, cfg_);

		return this
			.#backoff(this.#nbAttempts, cfg, (/** @type {any} */ backoffCfg) => {
				const deferred = this.$q.defer();
				try {
					/** @type {any} */
					const user = this.User.getSession();

					const cacheCalled = false;

					if (!cacheCalled) {
						backoffCfg.endpoint = user?.url?.[backoffCfg.endpoint] ?? '/';

						if (backoffCfg.endpoint === undefined) {
							deferred.reject('no endpoint');
						}
						else {
							/**
							 * loading timeout
							 *
							 * @private
							 */
							const loadingTimeout = this.$timeout(() => {
								this.$rootScope.waiting = true;
							}, 5000);

							/**
							 * standard timeout (using promise)
							 *
							 * @private
							 */
							const promiseTimeout = backoffCfg.promiseTimeout || this.$q.defer();

							/**
							 * request timeout
							 *
							 * @private
							 */
							const requestTimeout = this.$timeout(() => {
								promiseTimeout.resolve('timeout');
							}, backoffCfg.timeout);

							if (backoffCfg.promiseTimeout) {
								delete backoffCfg.timeout;
							}

							// users manually aborts request
							this.$rootScope.$on('API.abort', (event, reason) => {
								this.$rootScope.waiting = false;
								promiseTimeout.resolve(reason);
							});

							const url = urlcat(
								backoffCfg.endpoint ?? '/',
								'/api/v:version/:resource',
								{ version: Defaults.api.version, resource },
							);

							/** @type {any} */
							const headers = {};
							const localStorageToken = localStorage.getItem('token');
							const token = backoffCfg.token || user?.token?.id || localStorageToken;
							if (token) {
								headers[Defaults.api.headerTokenName] = token;
							}

							let apiContextId = backoffCfg.id;
							if (!apiContextId) {
								console.error(`API call "${resource}" has no "id" context value. Please specify one.`);
								apiContextId = '-';
							}

							// demande de fred
							// eslint-disable-next-line no-underscore-dangle
							parameters._action = resource;
							// eslint-disable-next-line no-underscore-dangle
							parameters._wrapper = 'v4';
							// eslint-disable-next-line no-underscore-dangle
							parameters._version = this.$rootScope.tag;
							// eslint-disable-next-line no-underscore-dangle
							parameters._url = this.$location.url();
							// eslint-disable-next-line no-underscore-dangle
							parameters._id = apiContextId;
							// eslint-disable-next-line no-underscore-dangle
							parameters._tokenid = headers[Defaults.api.headerTokenName];

							if (this.#nextRequestKParam) {
								parameters.kparam = Utils.encodeKParam(
									resource,
									headers[Defaults.api.headerTokenName],
									this.#nextRequestKParam,
								);
								this.#nextRequestKParam = null;
							}

							const request = {
								url,
								method: 'POST',
								data: parameters,
								headers,
								timeout: promiseTimeout.promise,
								/**
								 * @param {any} data
								 * @returns {Promise<any>}
								 */
								transformResponse(data) {
									let responseData;
									try {
										responseData = JSON.parse(data);
									}
									catch {
										if (typeof data === 'string' && (data.includes('<br />') || data.includes('Fatal error'))) {
											responseData = {
												error: {
													code:       -1,
													errno:      'Fatal error',
													errno_desc: data,
													file:       '',
													line:       -1,
													msg:        data,
													type:       'error',
												},
											};
										}
										else {
											/* TODO: certaines API retournent une string contenant un file... donc pas d'erreur ou de json !
											cf ClassDesignerExport
											 */

											/** @type {import('@knitiv/api-client-javascript').ErrorResultV4} */
											/*
											const error = {
												error: {
													code: -1,
													errno: 'Unknown error',
													errno_desc: data,
													file: '',
													line: -1,
													msg: data,
													type: 'error',
												},
											};
											*/
											responseData = data;
										}
									}

									return responseData;
								},
							};

							const merged = _.merge(request, backoffCfg);

							this.$http(merged)
								.then(response => {
									this.$rootScope.waiting = false;
									this.$timeout.cancel(loadingTimeout);
									this.$timeout.cancel(requestTimeout);

									if (
										response.headers('content-type').includes('application/json')
										&& typeof response.data === 'object'
									) {
										/** @type {import('@knitiv/api-client-javascript').APIResponse<any>} */
										const responseData = response.data;
										if (responseData.error) {
											if ([
												'SESSION_TIMEOUT',
												'NO_TOKEN',
												'PASSWORD_MUST_CHANGE',
											].includes(responseData.error.errno)) {
												this.User.logout(responseData.error.errno);
											}
											else if (responseData.error.type !== 'notice' && !backoffCfg.hideNotification) {
												this.Notification({
													type: responseData.error.type,
													title: responseData.error.errno,
													message: responseData.error.errno_desc,
												});
											}
											deferred.reject(responseData.error);
										}
										else {
											deferred.resolve(responseData);
										}
										// });

										// eslint-disable-next-line no-underscore-dangle
										if ('_NR_replace' in responseData && Object.keys(responseData._NR_replace).length > 0) {
											// eslint-disable-next-line no-underscore-dangle
											this.$rootScope.$broadcast('Form.nrReplace', responseData._NR_replace);
										}
									}
									else {
										deferred.resolve(response);
									}
									return response;
								}).catch(error => {
									console.error('error', error);
									this.$rootScope.waiting = false;
									this.$timeout.cancel(loadingTimeout);
									this.$timeout.cancel(requestTimeout);

									promiseTimeout.reject();

									let xhrStatus;

									if (typeof error === 'object') {
										({ xhrStatus } = error);
										if (
											error.config
											&& 'timeout' in error.config
											&& typeof error.config.timeout === 'object'
											&& error.config.timeout.$$state
										) {
											xhrStatus = error.config.timeout.$$state.value;
										}
									}

									if (xhrStatus === 'abort') {
										this.Notification.warning({
											title: this.$translate.instant('error.unable-to-process'),
											message: this.$translate.instant('error.network-abort'),
											replaceMessage: true,
										});
									}
									else if (xhrStatus !== 'timeout' && xhrStatus !== 'abort' && error.status === -1) {
										console.error('Network error');
									}
									deferred.reject(error);
								});
						}
					}

					return deferred.promise;
				}
				catch (error) {
					console.error('error', error);
					deferred.reject('Unknown error');
				}
				return deferred;
			})
			.then((/** @type {any} */ response) => {
				if (response.token) {
					this.User.setAlive(response.token);
				}
				return response;
			})
			.catch((/** @type {any} */ error) => {
				console.error('e', error);
			});
	}
}
